import { Link } from '@tanstack/react-router';
import { LandingNav } from './landing/LandingNav';
import { FooterSection } from './landing/FooterSection';
import { ArrowLeft } from 'lucide-react';

export function Terms() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <LandingNav />
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Home
          </Link>

          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">Terms of Service</h1>
          <p className="text-sm text-muted-foreground mb-8">Last updated: March 2026</p>

          <div className="space-y-6 text-muted-foreground leading-relaxed">
            <p>
              These Terms of Service ("Terms") govern your access to and use of the EU ServiceLine
              platform ("Service") provided by Easton Utilities ("we", "us", or "our"). By
              accessing or using the Service, you agree to be bound by these Terms.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">1. Acceptance of Terms</h2>
            <p>
              By accessing or using EU ServiceLine, you acknowledge that you have read,
              understood, and agree to be bound by these Terms. If you are using the Service on
              behalf of an organization, you represent that you have the authority to bind that
              organization to these Terms.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">2. Description of Service</h2>
            <p>
              EU ServiceLine is a multi-tenant helpdesk platform that provides ticket management,
              customer support tools, knowledge base, automation, and related services. Access to
              the Service is provided on a per-organization basis and is not available through
              self-registration.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">3. Account Responsibilities</h2>
            <p>You are responsible for:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Ensuring that your use of the Service complies with applicable laws and regulations</li>
              <li>Providing accurate and up-to-date information in your account profile</li>
            </ul>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">4. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
              <li>Attempt to gain unauthorized access to any part of the Service or its related systems</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Upload or transmit any malicious code, viruses, or harmful content</li>
              <li>Use the Service to send unsolicited communications or spam</li>
            </ul>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">5. Data Ownership</h2>
            <p>
              You retain ownership of all data you submit to the Service, including ticket
              content, customer information, and knowledge base articles. We do not claim
              ownership of your data. Each organization's data is isolated and accessible only
              to authorized members of that organization.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">6. Service Availability</h2>
            <p>
              We strive to maintain high availability of the Service but do not guarantee
              uninterrupted access. We may perform scheduled maintenance, updates, or
              modifications to the Service, which may temporarily affect availability.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">7. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Easton Utilities shall not be liable for
              any indirect, incidental, special, consequential, or punitive damages resulting
              from your use of or inability to use the Service.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">8. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your access to the Service at any
              time for violation of these Terms or for any other reason at our sole discretion.
              Upon termination, your right to use the Service will immediately cease.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">9. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. We will notify you of material changes by
              posting the updated Terms on this page and updating the "Last updated" date.
              Continued use of the Service after changes constitutes acceptance of the modified Terms.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">10. Contact</h2>
            <p>
              If you have questions about these Terms, please{' '}
              <Link to="/" hash="contact" className="text-primary hover:underline">
                contact us
              </Link>.
            </p>
          </div>
        </div>
      </main>
      <FooterSection />
    </div>
  );
}
